import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import lesson_reducer from './lesson-slice';
import note_reducer from './note-slice';
import analytics_reducer from './analytics-slice';
import schedule_reducer from './schedule-slice';
import theme_reducer from './theme-slice';
import student_reducer from './student-slice';
import meeting_reducer from './meeting-slice';
import tutor_reducer from './tutor-slice';
import quiz_reducer from './quiz-slice';
import auth_reducer from './auth-slice';
import walkthrough_reducer from './walkthrough-slice';
import highlights_reducer from './highlights-slice';
import classroom_reducer from './classroom-slice';
import payment_reducer from './payment-slice';
import gradingReducer from './grading-slice';
import gamificationReducer from './gamification-slice';
import engagementReducer from './engagement-slice';
import resourceLibraryReducer from './resource-library-slice';
import progressMonitoringReducer from './progress-monitoring-slice';
import messageReducer from './message-slice';
import communityReducer from './community-slice';
import sessionReducer from './session-slice';
import tutorActivityReducer from './tutor-activity-slice';
import studentLessonsReducer from './student-lessons-slice';
import referencesReducer from './references-slice';
import studioBookmarksReducer from './studio-bookmarks-slice';
import lessonCycleReducer from './lesson-cycle-slice';
import { cacheInvalidationMiddleware } from './middleware/cacheInvalidationMiddleware';
import { dataValidationMiddleware } from './middleware/dataValidationMiddleware';
const root_reducer = combineReducers({
    lessons: lesson_reducer,
    notes: note_reducer,
    analytics: analytics_reducer,
    schedule: schedule_reducer,
    theme: theme_reducer,
    student: student_reducer,
    meeting: meeting_reducer,
    tutors: tutor_reducer,
    quiz: quiz_reducer,
    auth: auth_reducer,
    walkthrough: walkthrough_reducer,
    highlights: highlights_reducer,
    classroom: classroom_reducer,
    payments: payment_reducer,
    grading: gradingReducer,
    gamification: gamificationReducer,
    engagement: engagementReducer,
    resourceLibrary: resourceLibraryReducer,
    progressMonitoring: progressMonitoringReducer,
    messages: messageReducer,
    community: communityReducer,
    session: sessionReducer,
    tutorActivity: tutorActivityReducer,
    studentLessons: studentLessonsReducer,
    references: referencesReducer,
    studioBookmarks: studioBookmarksReducer,
    lessonCycle: lessonCycleReducer,
});
/**
 * Reset transient loading/error fields on persisted slices.
 *
 * Without this, a slice that was mid-fetch when the user closed the tab is
 * rehydrated with `status: 'loading'` (or `is_loading: true`) and any UI that
 * gates on that flag shows the skeleton/spinner forever — until a new fetch
 * completes. This is the cause of the "Loading..." cards visible after a
 * cold reload on tutor and student dashboards.
 *
 * Run from `migrate` so it executes once per rehydrate, before any reducers
 * see the state.
 */
const TRANSIENT_FIELD_RESETS = {
    // Each entry: slice key → field overrides applied during rehydrate
    lessons: { is_loading: false, error: null },
    notes: { status: 'idle', error: null },
    quiz: { status: 'idle', error: null, isLoading: false },
    schedule: { status: 'idle', is_loading: false, error: null },
    student: { status: 'idle', error: null, isLoading: false },
    tutors: { status: 'idle', error: null, isLoading: false },
    highlights: { status: 'idle', error: null, isLoading: false },
    gamification: { status: 'idle', error: null, isLoading: false },
    auth: { loading: 'idle' },
    references: {
        libraryStatus: 'idle',
        detailStatus: 'idle',
        libraryError: null,
        detailError: null,
        libraryHasMore: false,
    },
};
function clearStaleLoadingFlags(state) {
    if (!state || typeof state !== 'object')
        return state;
    const next = { ...state };
    for (const sliceKey of Object.keys(TRANSIENT_FIELD_RESETS)) {
        const sliceState = next[sliceKey];
        if (!sliceState || typeof sliceState !== 'object')
            continue;
        const overrides = TRANSIENT_FIELD_RESETS[sliceKey];
        const merged = { ...sliceState };
        for (const field of Object.keys(overrides)) {
            // Only reset fields that actually exist on the slice — avoids creating
            // unexpected new keys on slices that don't track that field.
            if (field in merged) {
                merged[field] = overrides[field];
            }
        }
        next[sliceKey] = merged;
    }
    return next;
}
// Configure persistence
const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['auth', 'lessons', 'notes', 'quiz', 'theme', 'gamification', 'highlights', 'schedule', 'student', 'tutors'],
    blacklist: ['analytics', 'meeting', 'classroom', 'payments', 'grading', 'engagement', 'resourceLibrary', 'progressMonitoring', 'messages', 'community', 'tutorActivity', 'studentLessons', 'references', 'studioBookmarks', 'lessonCycle'],
    // Bump version so any clients with the previous shape go through migrate
    // even if they had no version recorded.
    version: 2,
    migrate: (state) => {
        // Always strip transient loading/error flags on rehydrate, regardless of
        // version. This is the fix for "skeletons stuck on first paint after a
        // cold reload": if a tab was closed mid-fetch the persisted state had
        // status='loading' / is_loading=true and components never re-rendered
        // out of skeleton mode until a new fetch resolved.
        return Promise.resolve(clearStaleLoadingFlags(state));
    },
    debug: process.env.NODE_ENV === 'development',
};
const persistedReducer = persistReducer(persistConfig, root_reducer);
const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
        serializableCheck: {
            ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
    }).concat(cacheInvalidationMiddleware, dataValidationMiddleware),
});
export const persistor = persistStore(store);
// Typed hooks
import { useDispatch, useSelector } from 'react-redux';
export const useAppDispatch = () => useDispatch();
export const useAppSelector = useSelector;
export { store }; // Add named export
export default store;
